/**
 * Candidate journey diagnostics.
 *
 * Extracted verbatim (behaviour-wise) from ScreeningResumeUpload.js, where this
 * instrumentation is what finally identified the multipart-boundary upload bug.
 * The resume step was the only stage with this level of reporting, so failures
 * anywhere else in the journey — document upload, details submit, assessment —
 * were invisible in exactly the same way resume upload used to be.
 *
 * WHAT MADE THIS WORK, and the thing to preserve when adding events:
 *
 * The volume of logging was never the point. What cracked the upload bug was a
 * handful of *discriminating* fields — `sentBytes=0` with `durationMs=35` proved
 * the request died before transmission, which ruled out timeouts, the network,
 * and the server in one line. A log that says "upload failed" discriminates
 * nothing and costs a debugging session.
 *
 * So every event here carries:
 *   - WHERE it happened   (stage + event)
 *   - WHAT the device is  (connection, context, capabilities)
 *   - WHY it failed       (bytes actually sent, elapsed ms, error code,
 *                          whether the device went offline mid-flight)
 *
 * Rules this module holds to:
 *   - Never throw. Telemetry must not be able to break a candidate's journey.
 *   - Never block. Fire-and-forget only; no awaiting a diagnostic.
 *   - Never spam. Callers dedupe repeated events (see `once`).
 */

import axios from 'axios';

/**
 * Everything the browser can tell us about this device's connection, right now.
 *
 * `navigator.connection` is Chromium-only, which is fine: the candidates hitting
 * this are on Android Chrome and Android WebViews. Where it is missing the keys
 * are simply absent rather than guessed at.
 */
export function readConnectionInfo() {
  if (typeof navigator === 'undefined') return {};
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const info = { online: navigator.onLine };
  if (!c) return info;
  if (c.effectiveType) info.effectiveType = c.effectiveType; // '4g' | '3g' | '2g' | 'slow-2g'
  if (typeof c.downlink === 'number') info.downlinkMbps = c.downlink;
  if (typeof c.rtt === 'number') info.rttMs = c.rtt;
  if (typeof c.saveData === 'boolean') info.saveData = c.saveData;
  if (c.type) info.netType = c.type; // 'wifi' | 'cellular' | ...
  return info;
}

/**
 * Names the browsing context, because "which app was this opened in" is one of
 * the few things that reliably separates candidates who succeed from ones who
 * do not, and a raw UA string is painful to eyeball in a log search.
 *
 * `desktopMode` matters specifically: Brave and several Android browsers spoof a
 * desktop UA while keeping a phone-sized screen, and that combination was the
 * fingerprint on the device where uploads failed. A viewport far wider than the
 * physical screen is the tell.
 */
export function readContextInfo() {
  if (typeof navigator === 'undefined') return {};
  const ua = navigator.userAgent || '';
  const info = {};
  // Android WebViews advertise themselves with `; wv)`. In-app browsers
  // (WhatsApp, Instagram, Facebook) are the usual hosts and each has its own
  // quirks around the file picker.
  if (/\bwv\b/.test(ua) || /Version\/[\d.]+ Chrome/.test(ua)) info.webview = true;
  const inApp = ua.match(
    /(FBAN|FBAV|Instagram|Line|WhatsApp|Snapchat|Twitter|MicroMessenger|HeyTapBrowser|OppoBrowser|SamsungBrowser|MiuiBrowser|Brave|OPR|UCBrowser)/i
  );
  if (inApp) info.inApp = inApp[1];
  if (typeof window !== 'undefined') {
    info.viewport = `${window.innerWidth}x${window.innerHeight}`;
    if (window.screen) {
      info.screen = `${window.screen.width}x${window.screen.height}`;
      // A desktop UA on a phone-sized screen: the request-desktop-site
      // fingerprint. Worth flagging explicitly rather than leaving it to be
      // spotted by comparing two other fields by eye.
      const looksDesktopUA = /X11|Windows NT|Macintosh/.test(ua) && !/Mobile/.test(ua);
      if (looksDesktopUA && window.screen.width && window.screen.width <= 500) {
        info.desktopMode = true;
      }
    }
    // A standalone display-mode means the link was opened from a home-screen
    // shortcut, which behaves differently again.
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) info.standalone = true;
  }
  if (navigator.deviceMemory) info.deviceMemoryGB = navigator.deviceMemory;
  if (navigator.hardwareConcurrency) info.cores = navigator.hardwareConcurrency;
  return info;
}

/**
 * Which browser APIs this device actually has.
 *
 * The candidate pages assume a modern browser throughout — FormData, Blob
 * .arrayBuffer, DataTransfer, sendBeacon, localStorage. On an old Android
 * WebView any of these can be missing or throw, and the failure surfaces as an
 * unrelated symptom (a dead upload box, a bounce to step one) with no clue as to
 * why. Recording the capability set once per page view means we can compare a
 * failing device against a working one directly, instead of theorising.
 */
export function readCapabilityInfo() {
  const caps = {};
  try {
    caps.hasFormData = typeof FormData !== 'undefined';
    caps.hasDataTransfer = typeof DataTransfer !== 'undefined';
    caps.hasBeacon = typeof navigator !== 'undefined' && !!navigator.sendBeacon;
    caps.hasFileArrayBuffer =
      typeof Blob !== 'undefined' && typeof Blob.prototype.arrayBuffer === 'function';
    caps.hasAbortController = typeof AbortController !== 'undefined';
    // localStorage is the one that bites hardest: it throws (not returns null)
    // in some WebViews and in private mode, and these pages depend on it to
    // recover candidate details after a refresh.
    try {
      const k = '__tc_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      caps.localStorage = 'ok';
    } catch (lsErr) {
      caps.localStorage = `blocked:${lsErr?.name || 'error'}`;
    }
    if (typeof navigator !== 'undefined' && navigator.storage?.estimate) {
      caps.storageApi = true;
    }
    caps.cookiesEnabled = typeof navigator !== 'undefined' ? navigator.cookieEnabled : null;
    if (typeof Intl !== 'undefined') {
      caps.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  } catch (_) {
    /* capability probing must never break the page */
  }
  return caps;
}

/**
 * Normalises an axios/network error into the fields that actually discriminate
 * between causes. This is the heart of why the resume instrumentation worked.
 *
 * `sentBytes` is the single most valuable field: zero bytes with a sub-200ms
 * duration means the request never left the browser (a rejected/blocked request
 * — malformed header, extension, CSP, proxy), whereas partial bytes over a long
 * duration means a genuinely dropped connection. Those two look identical in the
 * UI and identical in a naive "upload failed" log.
 */
export function describeRequestFailure(err, { startedAt, sentBytes = 0, totalBytes = 0 } = {}) {
  const durationMs = startedAt ? Date.now() - startedAt : null;
  return {
    durationMs,
    sentBytes,
    totalBytes,
    uplinkKbps: durationMs > 0 && sentBytes > 0 ? Math.round((sentBytes * 8) / durationMs) : 0,
    errorCode: err?.code || null,
    errorMessage: err?.message || null,
    httpStatus: err?.response?.status ?? null,
    // The server's own reason ("Document number is required for …"). Without it a 4xx
    // was logged only as "Request failed with status code 400", which says nothing.
    serverMessage: String(err?.response?.data?.message || err?.response?.data?.error || '').slice(0, 200) || null,
    // No response object at all means the failure never reached HTTP.
    isNetworkFailure: !err?.response,
    isTimeout: err?.code === 'ECONNABORTED',
    wentOffline: typeof navigator !== 'undefined' ? navigator.onLine === false : null,
  };
}

const firedOnce = new Set();

/**
 * Fire-and-forget diagnostic report.
 *
 * Uses sendBeacon when the page may be going away — a normal XHR is cancelled
 * on unload, which would lose exactly the reports that matter most (the
 * candidate who closed the tab mid-upload). Falls back to axios elsewhere.
 * Never throws and never blocks: a candidate must never be held up, or shown an
 * error, because telemetry failed.
 *
 * @param {string}  screeningId
 * @param {string}  stage   journey stage: 'resume' | 'documents' | 'details' | 'assessment'
 * @param {string}  event   e.g. 'upload_attempt_failed'
 * @param {object}  detail  event-specific fields (bytes, ids, counts)
 * @param {object}  identity { email, name }
 * @param {object}  opts    { beacon, once } — `once` dedupes by key for this page view
 */
export function reportDiag(screeningId, stage, event, detail, identity, opts = {}) {
  if (!screeningId) return;
  const { beacon = false, once = null } = opts;
  try {
    if (once) {
      // A candidate whose picker never opens taps Continue repeatedly; one such
      // candidate produced ~50 identical reports in 30 seconds, which buries
      // every other line in the log and adds nothing after the first.
      const key = `${screeningId}:${stage}:${event}:${once}`;
      if (firedOnce.has(key)) return;
      firedOnce.add(key);
    }
    const payload = {
      event: stage ? `${stage}:${event}` : event,
      email: identity?.email || '',
      name: identity?.name || '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      detail: { ...readConnectionInfo(), ...readContextInfo(), ...detail },
    };
    const url = `/api/screenings/${screeningId}/apply-diag`;
    if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    axios.post(url, payload).catch(() => {
      /* diagnostics only — never block the candidate on this */
    });
  } catch (_) {
    /* diagnostics must never break the page */
  }
}

/**
 * One-line page-open report: capabilities are only worth sending once per view,
 * and having them on the first line makes every later failure comparable against
 * a known-good device.
 */
export function reportPageOpen(screeningId, stage, identity, extra = {}) {
  reportDiag(
    screeningId,
    stage,
    'page_open',
    { ...readCapabilityInfo(), referrer: document?.referrer || 'none', ...extra },
    identity,
    { once: 'page_open' }
  );
}

/**
 * Wraps a request so its progress is tracked and any failure is described with
 * the discriminating fields above. Returns the axios response.
 *
 * Use this for anything carrying a file: it is the only way `sentBytes` gets
 * captured, and `sentBytes` is what tells a blocked request apart from a dropped
 * one.
 */
export async function postWithDiag(
  url,
  body,
  { screeningId, stage, event, identity, detail = {}, axiosConfig = {} } = {}
) {
  const startedAt = Date.now();
  let sentBytes = 0;
  let totalBytes = 0;
  try {
    const res = await axios.post(url, body, {
      ...axiosConfig,
      onUploadProgress: (evt) => {
        sentBytes = evt.loaded || 0;
        totalBytes = evt.total || 0;
        axiosConfig.onUploadProgress?.(evt);
      },
    });
    return res;
  } catch (err) {
    reportDiag(
      screeningId,
      stage,
      `${event}_failed`,
      { ...detail, ...describeRequestFailure(err, { startedAt, sentBytes, totalBytes }) },
      identity
    );
    throw err;
  }
}
